import React, { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "react-query";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  CircularProgress,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import RemoveRoundedIcon from "@mui/icons-material/RemoveRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import { toast } from "react-toastify";

import TextView from "../../../components/TextView";
import Button from "../../../components/Button";
import { colors } from "../../../utils";
import { getLoggedInUser } from "../../../utils/helpers";
import { getAllUserWheels } from "../../../services/api";
import { useAttachCaptureItem, useDetachCaptureItem } from "../../../services/capture";
import { isSyncedCloneUneditable } from "../../../components/WheelCardThreeDotMenu/wheelEditRouting";
import { ATTACH_WHEEL_MODAL_HEADING } from "../captureLabels";
import {
  itemIsOnWheel,
  validateWheelPickerSave,
  planBulkAttachToWheels,
  planBulkDetachFromWheels,
  planWheelPickerSave,
  isAlreadyAttachedError,
  bulkAttachResultMessage,
  bulkDetachResultMessage,
  toggleSelected,
  isWheelSelected,
  bulkWheelPartialAttachStatus,
  headerCheckboxState,
} from "../Capture.helpers";
import {
  normalizeWheels,
  applySearchAndSort,
  SORT_OPTIONS,
} from "./captureWheelPicker";
import PickerWheelThumb from "./PickerWheelThumb";
import {
  ModalBody,
  WheelList,
  WheelRow,
  ModalActions,
  BulkWheelModalActions,
  BulkSelectionBar,
  BulkSelectionCopy,
  BulkSelectionCancel,
  ActionBtnWrap,
  StateWrap,
  AttachedChip,
  PartialAttachChip,
  AttachHint,
  SearchWrap,
  SearchInput,
  AttachSortSelect,
  AttachPickerToolbar,
} from "../styles";

const StyledDialog = styled(Dialog)(() => ({
  ".MuiBackdrop-root": { backgroundColor: "rgba(0, 0, 0, 0.8)" },
  ".MuiPaper-root": {
    padding: "8px 8px 16px",
    border: "1px solid #E1E1E1",
    borderRadius: 12,
    width: 560,
    maxWidth: "92vw",
  },
}));

const CheckMark = styled("span", {
  shouldForwardProp: (prop) => prop !== "checked" && prop !== "mixed",
})(({ checked, mixed }) => ({
  width: 22,
  height: 22,
  flexShrink: 0,
  boxSizing: "border-box",
  border: `2px solid ${
    checked || mixed ? colors.PrimaryPurple500 : colors.darkGrey
  }`,
  backgroundColor: checked || mixed ? colors.PrimaryPurple500 : colors.white,
  borderRadius: 5,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  color: colors.white,
}));

const AttachWheelModal = ({ open, item, items, title = ATTACH_WHEEL_MODAL_HEADING, onClose, onAttached }) => {
  const userId = getLoggedInUser()?.id;
  const attach = useAttachCaptureItem();
  const detach = useDetachCaptureItem();
  const busy = attach.isLoading || detach.isLoading;
  const [selected, setSelected] = useState([]);
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("name");
  const selectionInitialized = useRef(false);
  const initialWheelIds = useRef([]);

  const list = useMemo(
    () => (Array.isArray(items) ? items : item ? [item] : []),
    [items, item]
  );
  const isBulk = list.length > 1;

  useEffect(() => {
    if (open) {
      setSelected([]);
      selectionInitialized.current = false;
      initialWheelIds.current = [];
      setSearch("");
      setSortBy("name");
    }
  }, [open]);

  const { data, isLoading, isError, refetch } = useQuery(
    ["captureAttachWheels", userId],
    () => getAllUserWheels(userId),
    { enabled: open && !!userId, refetchOnWindowFocus: false }
  );

  // Sync clones stay live-linked to their source wheel and are never
  // editable by the person who cloned them (same rule as the gallery card's
  // Edit-menu gate), so they can't be attach targets here either. A regular
  // unlocked clone is a normal editable wheel and still shows.
  const wheels = useMemo(
    () => normalizeWheels(data, { filter: (w) => !isSyncedCloneUneditable(w) }),
    [data]
  );
  const visibleWheels = useMemo(
    () => applySearchAndSort(wheels, search, sortBy),
    [wheels, search, sortBy]
  );

  useEffect(() => {
    if (
      !open ||
      isLoading ||
      wheels.length === 0 ||
      selectionInitialized.current
    ) {
      return;
    }
    selectionInitialized.current = true;
    if (isBulk) {
      const preselected = wheels
        .filter((wheel) => {
          const status = bulkWheelPartialAttachStatus(list, wheel.id);
          return status && status.attached > 0;
        })
        .map((wheel) => wheel.id);
      initialWheelIds.current = [];
      setSelected(preselected);
      return;
    }
    const preselected = wheels
      .filter((wheel) => itemIsOnWheel(list[0], wheel.id))
      .map((wheel) => wheel.id);
    initialWheelIds.current = [...preselected];
    setSelected(preselected);
  }, [open, isLoading, wheels, list, isBulk]);

  const selectedIds = useMemo(
    () => (Array.isArray(selected) ? selected : []),
    [selected]
  );
  const { attachPairs, detachPairs } = planWheelPickerSave(
    list,
    initialWheelIds.current,
    selectedIds
  );

  const saveError = validateWheelPickerSave({
    selected: selectedIds,
    initialWheelIds: initialWheelIds.current,
    wheelsLoading: isLoading,
    wheelsError: isError,
    wheelCount: wheels.length,
  });

  const visibleSelectedCount = visibleWheels.filter((wheel) =>
    isWheelSelected(selectedIds, wheel.id)
  ).length;
  const selectAllState = headerCheckboxState(
    visibleSelectedCount,
    visibleWheels.length
  );

  const bulkAttachPlan = useMemo(
    () => planBulkAttachToWheels(list, selectedIds),
    [list, selectedIds]
  );
  const bulkDetachPlan = useMemo(
    () => planBulkDetachFromWheels(list, selectedIds),
    [list, selectedIds]
  );

  const bulkBaseDisabled =
    busy || isLoading || isError || list.length === 0 || selectedIds.length === 0;
  const bulkAttachDisabled =
    bulkBaseDisabled || bulkAttachPlan.pairs.length === 0;
  const bulkDetachDisabled =
    bulkBaseDisabled || bulkDetachPlan.pairs.length === 0;
  const attachSelectedLabel =
    bulkAttachPlan.pairs.length > 0
      ? `Attach ${bulkAttachPlan.pairs.length}`
      : "Attach Selected";
  const detachSelectedLabel =
    bulkDetachPlan.linkCount > 0
      ? `Detach ${bulkDetachPlan.linkCount}`
      : "Detach Selected";
  const bulkSelectionError =
    isLoading || isError || wheels.length === 0 ? saveError : saveError;

  const toggleSelectAll = () => {
    const visibleWheelIds = visibleWheels.map((wheel) => wheel.id);
    if (selectAllState === "all") {
      const drop = new Set(visibleWheelIds.map(String));
      setSelected((prev) =>
        (Array.isArray(prev) ? prev : []).filter((id) => !drop.has(String(id)))
      );
    } else {
      setSelected((prev) => {
        const next = new Set((Array.isArray(prev) ? prev : []).map(String));
        visibleWheelIds.forEach((id) => next.add(String(id)));
        return wheels.filter((wheel) => next.has(String(wheel.id))).map((wheel) => wheel.id);
      });
    }
  };

  const handleClose = () => {
    if (busy) return;
    onClose();
  };

  const runPickerMutations = async ({ attachPairs: toAttach, detachPairs: toDetach }) => {
    if (toAttach.length === 0 && toDetach.length === 0) {
      toast.info("No changes to save.");
      return false;
    }

    const detachSettled = await Promise.allSettled(
      toDetach.map(({ item: it, wheelIds }) =>
        detach.mutateAsync({ id: it.id, wheelIds })
      )
    );
    const attachSettled = await Promise.allSettled(
      toAttach.map(({ item: it, wheelId }) =>
        attach.mutateAsync({ id: it.id, wheelId })
      )
    );

    const detached = detachSettled.filter((r) => r.status === "fulfilled").length;
    const detachFailed = detachSettled.filter((r) => r.status === "rejected").length;
    const attached = attachSettled.filter(
      (r) => r.status === "fulfilled" || isAlreadyAttachedError(r.reason)
    ).length;
    const attachFailed = attachSettled.filter(
      (r) => r.status === "rejected" && !isAlreadyAttachedError(r.reason)
    ).length;

    detachSettled
      .filter((r) => r.status === "rejected")
      .forEach((r) => console.error("Detach capture failed:", r.reason));
    attachSettled
      .filter((r) => r.status === "rejected")
      .forEach((r) => console.error("Attach capture failed:", r.reason));

    const totalFailed = detachFailed + attachFailed;
    if (totalFailed === 0) {
      if (detached > 0 && attached > 0) {
        toast.success("Capture updated.");
      } else if (detached > 0) {
        toast.success(detached === 1 ? "Link removed." : "Links removed.");
      } else if (attached > 0) {
        const { tone, message } = bulkAttachResultMessage({
          attached,
          alreadyOnTarget: 0,
          failed: 0,
          wheelCount: toAttach.length,
        });
        toast[tone](message);
      }
      return true;
    }

    if (detached === 0 && attached === 0) {
      toast.error("Could not update. Please try again.");
      return false;
    }

    toast.warning("Some changes could not be saved — please try again.");
    return true;
  };

  const handleConfirm = async () => {
    if (saveError) {
      toast.error(saveError);
      return;
    }

    if (attachPairs.length === 0 && detachPairs.length === 0) {
      (onAttached || onClose)();
      return;
    }

    const ok = await runPickerMutations({ attachPairs, detachPairs });
    if (ok) (onAttached || onClose)();
  };

  const runBulkAttach = async () => {
    const wheelIds = selectedIds.filter((id) => id != null);
    const { pairs, alreadyOnTargetCount } = planBulkAttachToWheels(list, wheelIds);
    const attachWheelCount = [...new Set(pairs.map((p) => p.wheelId))].length;
    if (pairs.length === 0) {
      toast.info("Every selected capture is already on the selected wheels.");
      return;
    }

    const settled = await Promise.allSettled(
      pairs.map(({ item: it, wheelId }) => attach.mutateAsync({ id: it.id, wheelId }))
    );

    const attached = settled.filter(
      (r) => r.status === "fulfilled" || isAlreadyAttachedError(r.reason)
    ).length;
    const realFailures = settled.filter(
      (r) => r.status === "rejected" && !isAlreadyAttachedError(r.reason)
    );

    settled
      .filter((r) => r.status === "rejected")
      .forEach((r) => console.error("Attach capture failed:", r.reason));

    const { tone, message } = bulkAttachResultMessage({
      attached,
      alreadyOnTarget: alreadyOnTargetCount,
      failed: realFailures.length,
      wheelCount: attachWheelCount,
      captureCount: list.length >= 2 ? list.length : undefined,
    });
    toast[tone](message);

    if (tone !== "error") (onAttached || onClose)();
  };

  const runBulkDetach = async () => {
    const wheelIds = selectedIds.filter((id) => id != null);
    const { pairs } = planBulkDetachFromWheels(list, wheelIds);
    const detachWheelCount = [...new Set(pairs.flatMap((p) => p.wheelIds))].length;
    if (pairs.length === 0) {
      toast.info("None of the selected captures are on the selected wheels.");
      return;
    }

    const settled = await Promise.allSettled(
      pairs.map(({ item: it, wheelIds }) =>
        detach.mutateAsync({ id: it.id, wheelIds })
      )
    );

    const detached = settled.filter((r) => r.status === "fulfilled").length;
    const failed = settled.filter((r) => r.status === "rejected").length;

    settled
      .filter((r) => r.status === "rejected")
      .forEach((r) => console.error("Detach capture failed:", r.reason));

    const { tone, message } = bulkDetachResultMessage({
      detached,
      failed,
      captureCount: list.length >= 2 ? list.length : undefined,
      wheelCount: detachWheelCount,
    });
    toast[tone](message);

    if (tone !== "error") (onAttached || onClose)();
  };

  const handleAttachSelectedClick = async () => {
    if (bulkAttachDisabled) return;
    await runBulkAttach();
  };

  const handleDetachSelectedClick = async () => {
    if (bulkDetachDisabled) return;
    await runBulkDetach();
  };

  return (
    <StyledDialog open={open} onClose={handleClose}>
      <DialogTitle>
        <TextView variant="title">{title}</TextView>
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{ position: "absolute", right: 8, top: 14, color: colors.darkGrey }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <ModalBody>
          {!isLoading && !isError && !isBulk && (
            <TextView variant="text" color={colors.textGrey}>
              Check wheels to attach this capture. Uncheck wheels to detach it.
            </TextView>
          )}
          {isLoading ? (
            <StateWrap style={{ minHeight: 200 }}>
              <CircularProgress
                size={28}
                style={{ color: colors.PrimaryPurple500 }}
              />
              <TextView variant="largeText" color={colors.textGrey}>
                Loading your wheels…
              </TextView>
            </StateWrap>
          ) : isError ? (
            <StateWrap style={{ minHeight: 200 }}>
              <TextView variant="largeText" color={colors.error}>
                Couldn't load wheels.
              </TextView>
              <ActionBtnWrap>
                <Button secondary onClick={() => refetch()}>
                  Retry
                </Button>
              </ActionBtnWrap>
            </StateWrap>
          ) : (
            <>
              {wheels.length > 0 && (
                <AttachPickerToolbar>
                  <SearchWrap>
                    <SearchRoundedIcon fontSize="small" />
                    <SearchInput
                      type="text"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      placeholder="Search wheels"
                      aria-label="Search wheels"
                    />
                  </SearchWrap>
                  <AttachSortSelect
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    aria-label="Sort wheels"
                  >
                    {SORT_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </AttachSortSelect>
                </AttachPickerToolbar>
              )}
              {isBulk && wheels.length > 0 && (
                <BulkSelectionBar>
                  <button
                    type="button"
                    onClick={toggleSelectAll}
                    style={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 12,
                      flex: 1,
                      minWidth: 0,
                      border: "none",
                      background: "transparent",
                      padding: 0,
                      cursor: "pointer",
                      textAlign: "left",
                      fontFamily: '"DM Sans", sans-serif',
                    }}
                  >
                    <CheckMark
                      checked={selectAllState === "all"}
                      mixed={selectAllState === "some"}
                    >
                      {selectAllState === "all" && (
                        <CheckRoundedIcon style={{ fontSize: 15 }} />
                      )}
                      {selectAllState === "some" && (
                        <RemoveRoundedIcon style={{ fontSize: 15 }} />
                      )}
                    </CheckMark>
                    <BulkSelectionCopy>
                      <TextView variant="largeText" color={colors.black}>
                        {list.length} capture{list.length === 1 ? "" : "s"} selected
                      </TextView>
                      <TextView variant="text" color={colors.textGrey}>
                        Choose the wheels you want to update
                      </TextView>
                    </BulkSelectionCopy>
                  </button>
                  <BulkSelectionCancel type="button" onClick={handleClose} disabled={busy}>
                    Cancel
                  </BulkSelectionCancel>
                </BulkSelectionBar>
              )}
              <WheelList>
              {wheels.length === 0 ? (
                <StateWrap style={{ minHeight: 160 }}>
                  <TextView variant="largeText" color={colors.textGrey}>
                    You don't have any wheels yet.
                  </TextView>
                </StateWrap>
              ) : visibleWheels.length === 0 ? (
                <StateWrap style={{ minHeight: 160 }}>
                  <TextView variant="largeText" color={colors.textGrey}>
                    No wheels match "{search}".
                  </TextView>
                </StateWrap>
              ) : (
                visibleWheels.map((wheel) => {
                  const operationSelected = isWheelSelected(selectedIds, wheel.id);
                  const bulkAttachStatus = isBulk
                    ? bulkWheelPartialAttachStatus(list, wheel.id)
                    : null;
                  const singleChecked = !isBulk && operationSelected;
                  const bulkCheckboxChecked = isBulk && operationSelected;
                  const showAttachedChip =
                    !isBulk && itemIsOnWheel(list[0], wheel.id);
                  const ariaChecked = isBulk ? operationSelected : singleChecked;
                  return (
                  <WheelRow
                    key={wheel.id}
                    type="button"
                    role="checkbox"
                    aria-checked={ariaChecked}
                    aria-label={
                      bulkAttachStatus
                        ? `${wheel.name || "Wheel"}, ${bulkAttachStatus.label}${
                            operationSelected ? ", selected for update" : ""
                          }`
                        : wheel.name || "Wheel"
                    }
                    active={operationSelected || undefined}
                    onClick={() => {
                      setSelected((prev) =>
                        toggleSelected(Array.isArray(prev) ? prev : [], wheel.id)
                      );
                    }}
                  >
                    <CheckMark
                      checked={isBulk ? bulkCheckboxChecked : singleChecked}
                    >
                      {(isBulk ? bulkCheckboxChecked : singleChecked) && (
                        <CheckRoundedIcon style={{ fontSize: 15 }} />
                      )}
                    </CheckMark>
                    <PickerWheelThumb wheel={wheel} />
                    <div
                      style={{
                        flex: 1,
                        minWidth: 0,
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,
                      }}
                    >
                      <TextView
                        variant="largeText"
                        color={colors.black}
                        style={{
                          display: "block",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {wheel.name}
                      </TextView>
                      {wheel.meta && (
                        <TextView variant="text" color={colors.textGrey}>
                          {wheel.meta}
                        </TextView>
                      )}
                    </div>
                    {bulkAttachStatus ? (
                      bulkAttachStatus.attached > 0 &&
                      bulkAttachStatus.attached === bulkAttachStatus.total ? (
                        <AttachedChip style={{ textTransform: "none" }}>
                          {bulkAttachStatus.label}
                        </AttachedChip>
                      ) : (
                        <PartialAttachChip style={{ textTransform: "none" }}>
                          {bulkAttachStatus.label}
                        </PartialAttachChip>
                      )
                    ) : (
                      showAttachedChip && (
                        <AttachedChip style={{ textTransform: "none" }}>
                          Media Already Added
                        </AttachedChip>
                      )
                    )}
                  </WheelRow>
                  );
                })
              )}
              </WheelList>
            </>
          )}

          {!!(isBulk ? bulkSelectionError : saveError) && !isLoading && (
            <AttachHint id="capture-attach-hint">
              <TextView variant="tag" color={colors.textGrey}>
                {isBulk ? bulkSelectionError : saveError}
              </TextView>
            </AttachHint>
          )}

          {isBulk ? (
            <BulkWheelModalActions>
              <Button
                onClick={handleAttachSelectedClick}
                isLoading={attach.isLoading}
                disabled={bulkAttachDisabled}
                aria-describedby={
                  bulkSelectionError && !isLoading ? "capture-attach-hint" : undefined
                }
              >
                {attachSelectedLabel}
              </Button>
              <Button
                variant="outlined"
                onClick={handleDetachSelectedClick}
                isLoading={detach.isLoading}
                disabled={bulkDetachDisabled}
                style={{
                  color: colors.primary,
                  borderColor: colors.primary,
                  backgroundColor: colors.white,
                }}
                aria-describedby={
                  bulkSelectionError && !isLoading ? "capture-attach-hint" : undefined
                }
              >
                {detachSelectedLabel}
              </Button>
            </BulkWheelModalActions>
          ) : (
          <ModalActions>
            <ActionBtnWrap>
              <Button secondary onClick={handleClose} disabled={busy}>
                Cancel
              </Button>
            </ActionBtnWrap>
            <ActionBtnWrap>
              <Button
                onClick={handleConfirm}
                isLoading={busy}
                disabled={busy || !!saveError}
                aria-describedby={
                  saveError && !isLoading ? "capture-attach-hint" : undefined
                }
              >
                Save
              </Button>
            </ActionBtnWrap>
          </ModalActions>
          )}
        </ModalBody>
      </DialogContent>
    </StyledDialog>
  );
};

export default AttachWheelModal;
